import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useOrganization } from '../../context/OrganizationContext'
import { useToast } from '../../context/ToastContext'
import { storesApi } from '../../services/storesApi'
import * as posAdminApi from '../../services/posAdminApi'
import * as storeSubscriptionAdminApi from '../../services/storeSubscriptionAdminApi'
import * as stripeConnectAdminApi from '../../services/stripeConnectAdminApi'
import * as paystackPayoutAdminApi from '../../services/paystackPayoutAdminApi'
import {
  listStoreHomepageSections,
  type HomepageSectionAdminRow,
  type VisibilityStatus,
} from '../../services/homepageSectionsAdminApi'
import { getCurrencyLabel } from '../../utils/currencies'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { FeatureToggle } from '../../components/ui/FeatureToggle'
import { SkeletonFormSections } from '../../components/ui/Skeleton'
import { appPossessive } from '../../config/brand'
import { buildAuditListSearch, storeAuditPath } from '../withdrawals/storeAuditRoutes'
import { SingleStoreSetupProgress } from '../../components/onboarding/PlatformStoreSetupProgress'
import { AdminTakeLivePanel } from '../../components/onboarding/AdminTakeLivePanel'
import { setupProgressAdminApi } from '../../services/setupProgressAdminApi'
import * as storeCustomDomainApi from '../../services/storeCustomDomainApi'
import { StoreSuspensionPanel } from '../../components/StoreSuspensionPanel'

function normalizeOrgCurrency(org) {
  return String(org?.currency ?? 'USD').trim().toUpperCase() || 'USD'
}

function effectiveMatchesOrgDefault(store, orgCurrency) {
  const r = String(store?.currency ?? '').trim().toUpperCase()
  return r === orgCurrency
}

function merchandisingStatusClass(status: VisibilityStatus): string {
  switch (status) {
    case 'active_now':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    case 'scheduled':
      return 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    case 'expired':
      return 'bg-muted text-muted-foreground'
    case 'disabled':
      return 'bg-destructive/15 text-destructive'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function formatMerchandisingSchedule(row: HomepageSectionAdminRow): string {
  if (!row.startDate && !row.endDate) return 'Always on'
  const start = row.startDate ? new Date(row.startDate).toLocaleString() : '—'
  const end = row.endDate ? new Date(row.endDate).toLocaleString() : '—'
  return `${start} → ${end}`
}

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  pending_mandate: 'Payment method required',
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Past due',
  expired: 'Expired',
  cancelled: 'Cancelled',
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  return d.toISOString().slice(0, 10)
}

function addUtcYearsYmd(years: number): string {
  const d = new Date()
  d.setUTCFullYear(d.getUTCFullYear() + years)
  return d.toISOString().slice(0, 10)
}

function isFreeStore(store: { platform?: string | null } | null, complimentary?: boolean): boolean {
  if (complimentary === true) return true
  return String(store?.platform ?? '').toUpperCase() === 'FREE'
}

function subscriptionStatusClass(status: string): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    case 'pending_mandate':
    case 'past_due':
      return 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    case 'expired':
    case 'cancelled':
      return 'bg-destructive/15 text-destructive'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function DetailBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm text-foreground">{children}</div>
    </div>
  )
}

export default function StoreDetail() {
  const { id: orgRouteId, storeId } = useParams()
  const { getOrganizationById } = useOrganization()
  const { pushToast } = useToast()
  const [org, setOrg] = useState(null)
  const [store, setStore] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [posSaving, setPosSaving] = useState(false)
  const [posError, setPosError] = useState<string | null>(null)
  const [homepageSections, setHomepageSections] = useState<HomepageSectionAdminRow[]>([])
  const [homepageSectionsError, setHomepageSectionsError] = useState<string | null>(null)
  const [subscriptionSnapshot, setSubscriptionSnapshot] =
    useState<storeSubscriptionAdminApi.StoreSubscriptionAdminSnapshot | null>(null)
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [renewLoading, setRenewLoading] = useState(false)
  const [complimentaryDate, setComplimentaryDate] = useState('')
  const [complimentarySaving, setComplimentarySaving] = useState(false)
  const [storeSetupProgress, setStoreSetupProgress] = useState(null)
  const [storeSetupProgressLoading, setStoreSetupProgressLoading] = useState(true)
  const [storeSetupProgressError, setStoreSetupProgressError] = useState<string | null>(null)
  const [stripeConnect, setStripeConnect] =
    useState<stripeConnectAdminApi.AdminStripeConnectStatus | null>(null)
  const [stripeConnectLoading, setStripeConnectLoading] = useState(true)
  const [stripeConnectError, setStripeConnectError] = useState<string | null>(null)
  const [stripeLinkBusy, setStripeLinkBusy] = useState(false)
  const [paystackPayout, setPaystackPayout] =
    useState<paystackPayoutAdminApi.AdminPaystackPayoutStatus | null>(null)
  const [paystackPayoutLoading, setPaystackPayoutLoading] = useState(true)
  const [paystackPayoutError, setPaystackPayoutError] = useState<string | null>(null)
  const [customDomainInput, setCustomDomainInput] = useState('')
  const [customDomainStatus, setCustomDomainStatus] =
    useState<storeCustomDomainApi.CustomDomainStatus>('none')
  const [customDomainSaving, setCustomDomainSaving] = useState(false)
  const [customDomainError, setCustomDomainError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const o = await getOrganizationById(orgRouteId)
        const { items } = await storesApi.list(orgRouteId)
        const sid = String(storeId)
        const s = (Array.isArray(items) ? items : []).find((row) => String(row.id) === sid)
        if (cancelled) return
        if (!s) {
          setLoadError('Store not found')
          return
        }
        setOrg(o)
        setStore(s)
        setCustomDomainInput(String(s.customDomain ?? '').trim())
        setCustomDomainStatus(
          (['none', 'pending', 'active', 'failed'].includes(String(s.customDomainStatus))
            ? String(s.customDomainStatus)
            : 'none') as storeCustomDomainApi.CustomDomainStatus,
        )
        setLoadError(null)
        setSubscriptionLoading(true)
        try {
          const sub = await storeSubscriptionAdminApi.getStoreSubscription(sid)
          if (!cancelled) {
            setSubscriptionSnapshot(sub)
            setComplimentaryDate(toDateInputValue(sub.subscription.trialEndsAt))
            setSubscriptionError(null)
          }
        } catch (subErr) {
          if (!cancelled) {
            setSubscriptionSnapshot(null)
            setSubscriptionError(
              subErr instanceof Error ? subErr.message : 'Failed to load subscription',
            )
          }
        } finally {
          if (!cancelled) setSubscriptionLoading(false)
        }
        setStripeConnectLoading(true)
        try {
          const sc = await stripeConnectAdminApi.getStoreStripeConnect(sid)
          if (!cancelled) {
            setStripeConnect(sc)
            setStripeConnectError(null)
          }
        } catch (scErr) {
          if (!cancelled) {
            setStripeConnect(null)
            setStripeConnectError(
              scErr instanceof Error ? scErr.message : 'Failed to load Stripe Connect',
            )
          }
        } finally {
          if (!cancelled) setStripeConnectLoading(false)
        }
        setPaystackPayoutLoading(true)
        try {
          const ps = await paystackPayoutAdminApi.getStorePaystackPayout(sid)
          if (!cancelled) {
            setPaystackPayout(ps)
            setPaystackPayoutError(null)
          }
        } catch (psErr) {
          if (!cancelled) {
            setPaystackPayout(null)
            setPaystackPayoutError(
              psErr instanceof Error ? psErr.message : 'Failed to load Paystack payout',
            )
          }
        } finally {
          if (!cancelled) setPaystackPayoutLoading(false)
        }
        try {
          const { items } = await listStoreHomepageSections(sid)
          if (!cancelled) {
            setHomepageSections(items)
            setHomepageSectionsError(null)
          }
        } catch (merchErr) {
          if (!cancelled) {
            setHomepageSections([])
            setHomepageSectionsError(
              merchErr instanceof Error ? merchErr.message : 'Failed to load homepage sections',
            )
          }
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgRouteId, storeId, getOrganizationById])

  useEffect(() => {
    if (!storeId) return
    let cancelled = false
    setStoreSetupProgressLoading(true)
    setStoreSetupProgressError(null)
    ;(async () => {
      try {
        const data = await setupProgressAdminApi.getStoreProgress(storeId)
        if (!cancelled) setStoreSetupProgress(data)
      } catch (e) {
        if (!cancelled) {
          setStoreSetupProgressError(e instanceof Error ? e.message : 'Failed to load setup progress')
        }
      } finally {
        if (!cancelled) setStoreSetupProgressLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [storeId])

  if (loadError) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">{loadError}</p>
        <Link to={orgRouteId ? `/organizations/${orgRouteId}` : '/organizations'}>
          <Button type="button" variant="secondary">
            Back
          </Button>
        </Link>
      </div>
    )
  }

  if (!store || !org) {
    return <SkeletonFormSections />
  }

  const orgCur = normalizeOrgCurrency(org)
  const effectiveCurrency = store.currency ? getCurrencyLabel(store.currency) : '—'
  const orgCurrencyLabel = org.currency ? getCurrencyLabel(org.currency) : '—'
  const usesOrgDefault = effectiveMatchesOrgDefault(store, orgCur)
  const remittancePath = `/organizations/${orgRouteId}/stores/${storeId}/pos-remittance`
  const posSalesPath = storeAuditPath(storeId, buildAuditListSearch({ channel: 'in_store' }))

  async function handleSaveCustomDomain() {
    if (!storeId) return
    setCustomDomainSaving(true)
    setCustomDomainError(null)
    try {
      const trimmed = customDomainInput.trim()
      const updated = await storeCustomDomainApi.patchStoreCustomDomain(storeId, {
        customDomain: trimmed === '' ? null : trimmed,
        customDomainStatus: trimmed === '' ? 'none' : customDomainStatus,
      })
      setStore((prev) => (prev ? { ...prev, ...updated } : updated))
      setCustomDomainInput(String(updated.customDomain ?? '').trim())
      setCustomDomainStatus(updated.customDomainStatus ?? 'none')
      pushToast({ message: 'Custom domain updated', variant: 'success' })
    } catch (e) {
      setCustomDomainError(e instanceof Error ? e.message : 'Failed to update custom domain')
    } finally {
      setCustomDomainSaving(false)
    }
  }

  async function handleClearCustomDomain() {
    if (!storeId) return
    setCustomDomainSaving(true)
    setCustomDomainError(null)
    try {
      const updated = await storeCustomDomainApi.patchStoreCustomDomain(storeId, {
        customDomain: null,
        customDomainStatus: 'none',
      })
      setStore((prev) => (prev ? { ...prev, ...updated } : updated))
      setCustomDomainInput('')
      setCustomDomainStatus('none')
      pushToast({ message: 'Custom domain cleared', variant: 'success' })
    } catch (e) {
      setCustomDomainError(e instanceof Error ? e.message : 'Failed to clear custom domain')
    } finally {
      setCustomDomainSaving(false)
    }
  }

  async function handlePosToggle(nextEnabled: boolean) {
    if (!store?.id) return
    setPosSaving(true)
    setPosError(null)
    try {
      await posAdminApi.patchStorePosSettings(store.id, nextEnabled)
      setStore((prev) => (prev ? { ...prev, posEnabled: nextEnabled } : prev))
      pushToast({
        message: nextEnabled ? 'In-store POS enabled' : 'In-store POS disabled',
        variant: 'success',
      })
    } catch (e) {
      setPosError(e instanceof Error ? e.message : 'Could not update POS setting')
    } finally {
      setPosSaving(false)
    }
  }

  async function applyComplimentarySnapshot(result: storeSubscriptionAdminApi.StoreSubscriptionAdminSnapshot) {
    setSubscriptionSnapshot(result)
    setComplimentaryDate(toDateInputValue(result.subscription.trialEndsAt))
  }

  async function handleComplimentarySave(trialEndsAt: string | null) {
    if (!store?.id) return
    setComplimentarySaving(true)
    setSubscriptionError(null)
    try {
      const result = await storeSubscriptionAdminApi.patchStoreComplimentaryAccess(store.id, trialEndsAt)
      await applyComplimentarySnapshot(result)
      pushToast({
        message:
          trialEndsAt == null
            ? 'Complimentary access is now unlimited'
            : 'Complimentary access end date saved',
        variant: 'success',
      })
    } catch (e) {
      setSubscriptionError(e instanceof Error ? e.message : 'Could not update complimentary access')
    } finally {
      setComplimentarySaving(false)
    }
  }

  async function handleRenewSubscription() {
    if (!store?.id) return
    setRenewLoading(true)
    setSubscriptionError(null)
    try {
      const result = await storeSubscriptionAdminApi.renewStoreSubscription(store.id)
      setSubscriptionSnapshot((prev) =>
        prev
          ? {
              ...prev,
              storeSubscriptionId: result.storeSubscriptionId,
              subscription: result.subscription,
            }
          : {
              storeId: result.storeId,
              storeSubscriptionId: result.storeSubscriptionId,
              source: 'store',
              subscription: result.subscription,
            },
      )
      const outcomeLabel =
        result.outcome === 'charged'
          ? 'Renewal charge initiated'
          : result.outcome === 'expired'
            ? 'Subscription marked expired'
            : 'Renewal skipped for current state'
      pushToast({ message: outcomeLabel, variant: result.outcome === 'charged' ? 'success' : 'info' })
    } catch (e) {
      setSubscriptionError(e instanceof Error ? e.message : 'Could not trigger renewal')
    } finally {
      setRenewLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Store</p>
          <h2 className="text-2xl font-semibold text-foreground">{store.name ?? '—'}</h2>
          <p className="text-muted-foreground">
            Organisation: {org.name} · Web address: <span className="font-mono">{store.subdomain ?? '—'}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/organizations/${orgRouteId}`}>
            <Button type="button" variant="secondary">
              Back to organisation
            </Button>
          </Link>
          <Link to={`/organizations/${orgRouteId}/stores/${storeId}/webhook-logs`}>
            <Button type="button" variant="secondary">
              Payment webhook logs
            </Button>
          </Link>
          <Link to={`/organizations/${orgRouteId}/stores/${storeId}/edit`}>
            <Button type="button">Edit store</Button>
          </Link>
        </div>
      </div>

      <SingleStoreSetupProgress
        data={storeSetupProgress}
        loading={storeSetupProgressLoading}
        error={storeSetupProgressError}
      />

      {storeId ? <AdminTakeLivePanel storeId={storeId} /> : null}

      <StoreSuspensionPanel
        storeId={store.id}
        storeName={store.name ?? 'This store'}
        suspendedAt={store.suspendedAt}
        suspensionReason={store.suspensionReason}
        onChanged={(next) =>
          setStore((prev) => (prev ? { ...prev, suspendedAt: next.suspendedAt, suspensionReason: next.suspensionReason } : prev))
        }
      />

      <Card>
        <CardHeader
          title="Store details"
          description="Read-only summary. Use Edit store to change fields."
        />
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailBlock label="Contact email">{store.contactEmail?.trim() ? store.contactEmail : '—'}</DetailBlock>
          <DetailBlock label="Contact phone">{store.contactPhone?.trim() ? store.contactPhone : '—'}</DetailBlock>
          <DetailBlock label="What this shop sells">
            {store.sellingSummary?.trim() ? store.sellingSummary : '—'}
          </DetailBlock>
          <DetailBlock label="Storefront currency (customers pay in)">{effectiveCurrency}</DetailBlock>
          <DetailBlock label="Company default currency">{orgCurrencyLabel}</DetailBlock>
          <DetailBlock label="Uses company default">{usesOrgDefault ? 'Yes' : 'No — store override'}</DetailBlock>
        </dl>
        {!usesOrgDefault ? (
          <div
            className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-950 dark:text-amber-100"
            role="status"
          >
            <p className="font-semibold">Store override active</p>
            <p className="mt-1">
              The online shop shows {effectiveCurrency}, not the company default ({orgCurrencyLabel}).
              Edit this store and choose “Use company default” if prices should follow the organisation.
            </p>
          </div>
        ) : null}
      </Card>

      <Card>
        <CardHeader
          title="Platform subscription"
          description={
            isFreeStore(store, subscriptionSnapshot?.subscription.complimentary)
              ? 'Complimentary FREE access. Unlimited until you set an end date. After expiry the merchant is gated and asked to add a card (charged if they complete). Extending restores FREE with no charge.'
              : 'Per-store SaaS billing (Mollie). Renewal charges run on schedule; use Renew now to trigger manually.'
          }
        />
        {subscriptionLoading ? (
          <SkeletonFormSections />
        ) : subscriptionError && !subscriptionSnapshot ? (
          <p className="text-sm text-destructive" role="alert">
            {subscriptionError}
          </p>
        ) : subscriptionSnapshot ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${subscriptionStatusClass(subscriptionSnapshot.subscription.status)}`}
              >
                {isFreeStore(store, subscriptionSnapshot.subscription.complimentary) &&
                subscriptionSnapshot.subscription.status === 'trialing'
                  ? 'Complimentary'
                  : (SUBSCRIPTION_STATUS_LABELS[subscriptionSnapshot.subscription.status] ??
                    subscriptionSnapshot.subscription.status)}
              </span>
              {store?.platform ? (
                <span className="text-xs text-muted-foreground">Platform: {String(store.platform)}</span>
              ) : null}
            </div>
            <dl className="grid gap-4 sm:grid-cols-2">
              <DetailBlock label="Plan">
                {subscriptionSnapshot.subscription.planName ?? subscriptionSnapshot.subscription.planCode}
                {subscriptionSnapshot.subscription.displayPrice
                  ? ` · ${subscriptionSnapshot.subscription.displayPrice}`
                  : ''}
                {' · '}
                {subscriptionSnapshot.subscription.billingInterval}
              </DetailBlock>
              <DetailBlock label="Billing required">
                {subscriptionSnapshot.subscription.billingRequired ? 'Yes' : 'No'}
              </DetailBlock>
              <DetailBlock
                label={
                  isFreeStore(store, subscriptionSnapshot.subscription.complimentary)
                    ? 'Complimentary access ends'
                    : 'Trial ends'
                }
              >
                {formatDateTime(subscriptionSnapshot.subscription.trialEndsAt)}
              </DetailBlock>
              <DetailBlock label="Current period ends">
                {formatDateTime(subscriptionSnapshot.subscription.currentPeriodEndsAt)}
              </DetailBlock>
              <DetailBlock label="Grace ends">{formatDateTime(subscriptionSnapshot.subscription.graceEndsAt)}</DetailBlock>
              <DetailBlock label="Billing">
                {subscriptionSnapshot.subscription.billingCurrency
                  ? `${subscriptionSnapshot.subscription.billingCurrency} · ${
                      subscriptionSnapshot.subscription.billingProvider ?? '—'
                    }${
                      subscriptionSnapshot.subscription.billingLockedAt
                        ? ` (locked ${formatDateTime(subscriptionSnapshot.subscription.billingLockedAt)})`
                        : ''
                    }`
                  : 'Not locked yet'}
              </DetailBlock>
              <DetailBlock label="Staff seats">
                {subscriptionSnapshot.subscription.staffCount}
                {subscriptionSnapshot.subscription.maxStaffCount != null
                  ? ` / ${subscriptionSnapshot.subscription.maxStaffCount}`
                  : ' (unlimited)'}
              </DetailBlock>
            </dl>
            {isFreeStore(store, subscriptionSnapshot.subscription.complimentary) ? (
              <div className="space-y-3 rounded-2xl border border-border bg-muted/30 p-4">
                <p className="text-sm font-medium text-foreground">Complimentary access ends</p>
                <p className="text-xs text-muted-foreground">
                  Leave empty and save Unlimited for no end date. After expiry they must add a card to continue
                  (that charges and moves the store to PAID). Extending a date restores FREE with no charge.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1">
                    <Input
                      id="complimentary-ends"
                      label="End date"
                      type="date"
                      value={complimentaryDate}
                      onChange={(e) => setComplimentaryDate(e.target.value)}
                      disabled={complimentarySaving}
                    />
                  </div>
                  <Button
                    type="button"
                    disabled={complimentarySaving}
                    onClick={() =>
                      void handleComplimentarySave(complimentaryDate.trim() === '' ? null : complimentaryDate)
                    }
                  >
                    {complimentarySaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={complimentarySaving}
                    onClick={() => void handleComplimentarySave(addUtcYearsYmd(1))}
                  >
                    1 year
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={complimentarySaving}
                    onClick={() => void handleComplimentarySave(addUtcYearsYmd(2))}
                  >
                    2 years
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={complimentarySaving}
                    onClick={() => void handleComplimentarySave(null)}
                  >
                    Unlimited
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={renewLoading || subscriptionSnapshot.storeSubscriptionId == null}
                  onClick={() => void handleRenewSubscription()}
                >
                  {renewLoading ? 'Processing…' : 'Renew now'}
                </Button>
              </div>
            )}
            {subscriptionError ? (
              <p className="text-sm text-destructive" role="alert">
                {subscriptionError}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No subscription data for this store.</p>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Stripe Connect"
          description="Express account status for customer checkout and wallet transfers. Merchants onboard from store admin; you can open the same support link."
        />
        {stripeConnectLoading ? (
          <SkeletonFormSections />
        ) : stripeConnectError && !stripeConnect ? (
          <p className="text-sm text-destructive" role="alert">
            {stripeConnectError}
          </p>
        ) : stripeConnect ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  stripeConnect.readyForCheckout
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {stripeConnect.readyForCheckout
                  ? 'Ready for checkout'
                  : stripeConnect.connectAccountId
                    ? 'Onboarding'
                    : 'Not connected'}
              </span>
              {!stripeConnect.currencyEligible ? (
                <span className="text-xs text-muted-foreground">Currency not on Stripe gateway</span>
              ) : null}
            </div>
            <dl className="grid gap-4 sm:grid-cols-2">
              <DetailBlock label="Account">{stripeConnect.connectAccountId ?? '—'}</DetailBlock>
              <DetailBlock label="Charges enabled">
                {stripeConnect.chargesEnabled ? 'Yes' : 'No'}
              </DetailBlock>
              <DetailBlock label="Payouts enabled">
                {stripeConnect.payoutsEnabled ? 'Yes' : 'No'}
              </DetailBlock>
              <DetailBlock label="Auto-withdraw">{stripeConnect.autoWithdraw}</DetailBlock>
            </dl>
            <Button
              type="button"
              variant="outline"
              disabled={stripeLinkBusy || !stripeConnect.stripeConfigured || !stripeConnect.currencyEligible}
              onClick={() => {
                void (async () => {
                  setStripeLinkBusy(true)
                  try {
                    const { url } = await stripeConnectAdminApi.createStoreStripeConnectAccountLink(
                      storeId!,
                    )
                    window.open(url, '_blank', 'noopener,noreferrer')
                  } catch (e) {
                    pushToast({
                      message: e instanceof Error ? e.message : 'Could not open onboarding link',
                      variant: 'error',
                    })
                  } finally {
                    setStripeLinkBusy(false)
                  }
                })()
              }}
            >
              {stripeLinkBusy ? 'Opening…' : 'Open onboarding link'}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No Stripe Connect data for this store.</p>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Paystack payouts"
          description="Bank recipient for NGN (and other Paystack) wallet withdrawals. Merchants set this up under Payouts in store admin."
        />
        {paystackPayoutLoading ? (
          <SkeletonFormSections />
        ) : paystackPayoutError && !paystackPayout ? (
          <p className="text-sm text-destructive" role="alert">
            {paystackPayoutError}
          </p>
        ) : paystackPayout ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  paystackPayout.readyForPayout
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {paystackPayout.readyForPayout ? 'Recipient ready' : 'Not set up'}
              </span>
              {!paystackPayout.currencyEligible ? (
                <span className="text-xs text-muted-foreground">Currency not on Paystack gateway</span>
              ) : null}
            </div>
            <dl className="grid gap-4 sm:grid-cols-2">
              <DetailBlock label="Account name">{paystackPayout.accountName ?? '—'}</DetailBlock>
              <DetailBlock label="Account">
                {paystackPayout.accountNumberLast4 ? `••••${paystackPayout.accountNumberLast4}` : '—'}
              </DetailBlock>
              <DetailBlock label="Bank code">{paystackPayout.bankCode ?? '—'}</DetailBlock>
              <DetailBlock label="Auto-withdraw">{paystackPayout.autoWithdraw}</DetailBlock>
            </dl>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No Paystack payout data for this store.</p>
        )}
      </Card>

      <Card>
        <CardHeader
          title="In-store POS"
          description="Enable point-of-sale for this store, review takings, and inspect in-store sales."
        />
        <div className="space-y-5">
          <FeatureToggle
            label="POS enabled"
            description={
              store.posEnabled
                ? 'Staff can record in-store sales in store admin.'
                : 'Enable POS to allow in-store sales.'
            }
            checked={Boolean(store.posEnabled)}
            onChange={(v) => void handlePosToggle(v)}
            disabled={posSaving}
          />
          {posError ? (
            <p className="text-sm text-destructive" role="alert">
              {posError}
            </p>
          ) : null}
          {!store.posEnabled ? (
            <p className="rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              Enable POS to allow in-store sales. Remittance and sales reports are unavailable until POS is on.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Link to={remittancePath}>
                <Button type="button" variant="secondary">
                  Remittance report
                </Button>
              </Link>
              <Link to={posSalesPath}>
                <Button type="button" variant="secondary">
                  In-store sales activity
                </Button>
              </Link>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader title="Online shop profile" description="Customer-facing storefront content." />
        <div className="space-y-4">
          <DetailBlock label="Tagline">{store.tagline?.trim() ? store.tagline : '—'}</DetailBlock>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">About</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
              {store.aboutText?.trim() ? store.aboutText : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Address</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
              {store.contactAddress?.trim() ? store.contactAddress : '—'}
            </p>
          </div>
          <DetailBlock label="Business hours">{store.businessHours?.trim() ? store.businessHours : '—'}</DetailBlock>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">About banner</p>
            <div className="mt-2 overflow-hidden rounded-xl border border-border bg-muted/50 p-3">
              {store.aboutBannerUrl && /^https?:\/\//i.test(store.aboutBannerUrl) ? (
                <img
                  src={store.aboutBannerUrl}
                  alt=""
                  className="max-h-48 w-full max-w-xl rounded-lg object-cover"
                />
              ) : (
                <p className="text-sm text-muted-foreground">No banner image</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Homepage merchandising (read-only)"
          description="Product and category blocks below hero/trust on the customer storefront. Merchants manage these in store admin."
        />
        {homepageSectionsError ? (
          <p className="text-sm text-destructive">{homepageSectionsError}</p>
        ) : homepageSections.length === 0 ? (
          <p className="text-sm text-muted-foreground">No homepage sections configured.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Schedule</th>
                </tr>
              </thead>
              <tbody>
                {homepageSections.map((row) => (
                  <tr key={row.id} className="border-b border-border/60">
                    <td className="px-3 py-2 font-medium">
                      {row.title ?? row.systemKey ?? '—'}
                      {row.isSystemGenerated ? (
                        <span className="ml-2 text-xs text-muted-foreground">(system)</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{row.sectionType}</td>
                    <td className="px-3 py-2">{row.sourceType}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${merchandisingStatusClass(row.visibilityStatus)}`}
                      >
                        {row.visibilityStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {formatMerchandisingSchedule(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Custom domain"
          description="Platform-only vanity hostname. Attach DNS/TLS first, then set status to Active so shoppers can use it."
        />
        <div className="space-y-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            <DetailBlock label="Platform subdomain">
              <span className="font-mono">{store.subdomain ?? '—'}</span>
            </DetailBlock>
            <DetailBlock label="Public URL">
              {store.publicUrl?.trim() ? (
                <a
                  href={store.publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {store.publicUrl}
                </a>
              ) : (
                '—'
              )}
            </DetailBlock>
            <DetailBlock label="Verified at">
              {formatDateTime(store.customDomainVerifiedAt)}
            </DetailBlock>
          </dl>
          {customDomainError ? (
            <p className="text-sm text-destructive" role="alert">
              {customDomainError}
            </p>
          ) : null}
          <Input
            id="custom-domain"
            label="Custom domain (hostname)"
            value={customDomainInput}
            onChange={(e) => setCustomDomainInput(e.target.value)}
            disabled={customDomainSaving}
            placeholder="www.merchant.com"
          />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Status
            </span>
            <select
              className="min-h-10 rounded-xl border border-border bg-card px-3 py-2"
              value={customDomainStatus}
              onChange={(e) =>
                setCustomDomainStatus(e.target.value as storeCustomDomainApi.CustomDomainStatus)
              }
              disabled={customDomainSaving}
            >
              <option value="none">None</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <p className="text-xs text-muted-foreground">
            Only Active domains resolve for shoppers. Pending/failed never serve traffic.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={customDomainSaving}
              onClick={() => void handleSaveCustomDomain()}
            >
              {customDomainSaving ? 'Saving…' : 'Save custom domain'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={customDomainSaving || (!customDomainInput && customDomainStatus === 'none')}
              onClick={() => void handleClearCustomDomain()}
            >
              Clear
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Customer mobile app"
          description={`Optional store-specific App Store / Play links; otherwise ${appPossessive()} defaults apply.`}
        />
        <dl className="grid gap-4 sm:grid-cols-1">
          <DetailBlock label="App Store (iOS)">
            {store.iosAppStoreUrl?.trim() ? (
              <a
                href={store.iosAppStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-4 hover:underline"
              >
                {store.iosAppStoreUrl}
              </a>
            ) : (
              '—'
            )}
          </DetailBlock>
          <DetailBlock label="Google Play (Android)">
            {store.androidPlayStoreUrl?.trim() ? (
              <a
                href={store.androidPlayStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-4 hover:underline"
              >
                {store.androidPlayStoreUrl}
              </a>
            ) : (
              '—'
            )}
          </DetailBlock>
        </dl>
      </Card>
    </div>
  )
}
